<template>
  <v-dialog v-model="showModal" max-width="460" persistent>
    <v-card class="am-card">
      <!-- Cabecera azul -->
      <div class="am-header">
        <div class="am-header-left">
          <div class="am-role-chip">
            <v-icon size="13" color="white">{{ subjectIcon }}</v-icon>
            <span>{{ subjectLabel }}</span>
          </div>
          <p class="am-task-name">{{ approvalData.taskName }}</p>
        </div>
        <v-btn icon dark x-small @click="closeModal" class="am-close-btn">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>
      
      <v-card-text class="am-body">
        <!-- Estado -->
        <div class="am-field-label">Estado</div>
        <div class="am-status-group">
          <button
            v-for="s in approvalStatuses"
            :key="s.value"
            class="am-status-btn"
            :class="{ 'am-status-btn--active': approvalData.estado === s.value, [`am-status-btn--${s.value.toLowerCase()}`]: true }"
            :disabled="loading"
            type="button"
            @click="approvalData.estado = s.value"
          >
            <v-icon size="14" class="mr-1">{{ s.icon }}</v-icon>
            {{ s.text }}
          </button>
        </div>

        <!-- Comentario -->
        <div class="am-field-label mt-3">Comentario <span class="am-optional">(opcional)</span></div>
        <v-textarea
          v-model="approvalData.comentario"
          placeholder="Agrega una observación..."
          outlined
          dense
          hide-details
          :disabled="loading"
          rows="3"
          class="am-textarea"
        ></v-textarea>

        <!-- Adjunto -->
        <div class="am-divider"></div>
        <div class="am-field-label">Adjunto</div>

        <!-- Input oculto -->
        <input
          ref="fileInput"
          type="file"
          accept="application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style="display: none"
          @change="onFileChange"
        />

        <!-- Preview de imagen si el adjunto existente es imagen y no se seleccionó nuevo archivo -->
        <div v-if="existingAttachment && isExistingImage && !file" class="am-image-preview-wrap">
          <a :href="existingAttachment.url" target="_blank" rel="noopener" class="am-image-link">
            <img :src="existingAttachment.url" :alt="existingAttachment.name" class="am-image-preview" />
            <div class="am-image-overlay">
              <v-icon size="18" color="white">mdi-open-in-new</v-icon>
            </div>
          </a>
          <div class="am-image-meta">
            <span class="am-attachment-name">{{ existingAttachment.name }}</span>
            <v-btn icon x-small color="error" @click="removeAttachment" :disabled="loading">
              <v-icon size="15">mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Adjunto existente no-imagen -->
        <div v-else-if="existingAttachment && !isExistingImage && !file" class="am-attachment">
          <v-icon size="16" color="#2D4B95" class="mr-2">mdi-file-document-outline</v-icon>
          <span class="am-attachment-name">{{ existingAttachment.name }}</span>
          <v-spacer></v-spacer>
          <v-btn icon x-small @click="downloadAttachment" :disabled="loading">
            <v-icon size="15">mdi-download</v-icon>
          </v-btn>
          <v-btn icon x-small color="error" @click="removeAttachment" :disabled="loading">
            <v-icon size="15">mdi-delete</v-icon>
          </v-btn>
        </div>

        <!-- Preview del nuevo archivo seleccionado (imagen) -->
        <div v-else-if="file && isNewFileImage" class="am-image-preview-wrap">
          <div class="am-image-link" style="cursor: default;">
            <img :src="newFilePreviewUrl" :alt="file.name" class="am-image-preview" />
          </div>
          <div class="am-image-meta">
            <span class="am-attachment-name">{{ file.name }}</span>
            <v-btn icon x-small color="error" @click="clearNewFile" :disabled="loading">
              <v-icon size="15">mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Preview del nuevo archivo seleccionado (no-imagen) -->
        <div v-else-if="file && !isNewFileImage" class="am-attachment">
          <v-icon size="16" color="#2D4B95" class="mr-2">mdi-file-document-outline</v-icon>
          <span class="am-attachment-name">{{ file.name }}</span>
          <v-spacer></v-spacer>
          <v-btn icon x-small color="error" @click="clearNewFile" :disabled="loading">
            <v-icon size="15">mdi-delete</v-icon>
          </v-btn>
        </div>

        <!-- Validación tamaño -->
        <div v-if="fileSizeError" class="am-file-error">
          <v-icon size="13" color="#c62828" class="mr-1">mdi-alert-circle-outline</v-icon>
          El archivo debe ser menor a 10 MB
        </div>

        <!-- Botón selector de archivo: solo visible cuando no hay adjunto -->
        <button
          v-if="!hasAttachment"
          type="button"
          class="am-upload-btn"
          :disabled="loading"
          @click="$refs.fileInput.click()"
        >
          <v-icon size="14" class="mr-1">mdi-paperclip</v-icon>
          Adjuntar archivo
        </button>

      </v-card-text>

      <!-- Acciones -->
      <div class="am-actions">
        <v-btn text small :disabled="loading" color="grey darken-1" @click="closeModal">Cancelar</v-btn>
        <v-btn small depressed color="#2D4B95" dark :loading="loading" @click="saveApproval">Guardar</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { setTaskApproval } from '@/helpers/api/entrega';
import EventBus from '@/helpers/event_bus'
import { v4 as uuidv4 } from 'uuid'
import _debounce from 'lodash/debounce'
import {create as createDraftAPI, update as updateDraftAPI} from '@/helpers/api/drafts'
import { deleteFile } from '@/helpers/api/fileManager'

export default {
  name: 'AprobacionModal',
  
  props: {
    aprobacion: {
      type: Object,
      default: null
    }
  },
  
  data() {
    return {
      loading: false,
      approvalData: {
        taskId: null,
        subjectId: null,
        subject: null,
        estado: 'Aprobado',
        comentario: ''
      },
      approvalStatuses: [
        { text: 'Aprobado', value: 'Aprobado', icon: 'mdi-check-circle-outline' },
        { text: 'Observado', value: 'Observado', icon: 'mdi-circle-half-full' },
        { text: 'Rechazado', value: 'Rechazado', icon: 'mdi-close-circle-outline' },
      ],
      file: null,
      fileSizeError: false,
      existingAttachment: null,
      fileChanged: false,
      newFilePreviewUrl: null
      ,
      // Draft/upload handling
      draftId: null,
      draftCreated: false,
      draftUpdateInterval: 20,
      draftSaveTimer: null,
      filesPreview: [],
      handleFormChangeDebounced: null,
    };
  },
  
  computed: {
    showModal() {
      return !!this.aprobacion;
    },
    
    subjectLabel() {
      return this.approvalData.subject?.name ?? 'ITO';
    },

    subjectIcon() {
      if (!this.approvalData.subject) return 'mdi-shield-check';
      return 'mdi-account-hard-hat';
    },

    isExistingImage() {
      if (!this.existingAttachment) return false;
      const name = (this.existingAttachment.name || '').toLowerCase();
      return /\.(jpg|jpeg|png|gif|webp|svg|bmp)$/.test(name);
    },

    isNewFileImage() {
      if (!this.file) return false;
      return this.file.type.startsWith('image/');
    },

    hasAttachment() {
      return !!(this.existingAttachment || this.file);
    }
    ,
    draftToLoad() { return this.$store.entregaStore.state.draftToLoad }
  },
  
  watch: {
    aprobacion(newVal) {
      if (newVal) {
        // The component remains mounted while the dialog is closed. Re-register
        // the upload listener (and the autosave debounce) every time it opens.
        this.mountedSetup()
        this.approvalData = {
          taskId: newVal.taskId || newVal.task_id,
          taskName: newVal.taskName || newVal.task_name,
          subjectId: newVal.subjectId || newVal.subject_id,
          subject: newVal.subject || null,
          entregaId: newVal.entregaId || newVal.entrega_id,
          estado: newVal.estado || 'Aprobado',
          comentario: newVal.comentario || ''
        };

        // Cargar adjunto directamente desde la prop
        const att = newVal.attachment?.file;
        this.existingAttachment = att
          ? { name: att.name || att.filename || 'archivo', url: att.url }
          : null;

        this.file = null;
        this.fileChanged = false;
        this.fileSizeError = false;
        this.newFilePreviewUrl = null;
        this.filesPreview = [];
        this.draftId = null;
        this.draftCreated = false;
      } else {
        this.resetForm();
        // stop autosave timer and pending debounce when modal is closed programmatically
        this.removeUploadListener()
        this.stopDraftUpdateTimer()
        if (this.handleFormChangeDebounced && this.handleFormChangeDebounced.cancel) {
          try { this.handleFormChangeDebounced.cancel() } catch (e) { console.log('Error cancelling debounce:', e) }
        }
        this.handleFormChangeDebounced = null
        this.draftId = null
        this.draftCreated = false
        this.filesPreview = []
      }
    }
    ,
    'approvalData.comentario'() {
      if (this.handleFormChangeDebounced) this.handleFormChangeDebounced()
    }
  },
  
  mounted() {
    this.mountedSetup()
  },

  beforeDestroy() {
    this.removeUploadListener()
    if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
  },

  methods: {
    resetForm() {
      this.approvalData = {
        taskId: null,
        subjectId: null,
        subject: null,
        entregaId: null,
        estado: 'Aprobado',
        comentario: ''
      };
      this.file = null;
      this.existingAttachment = null;
      this.fileChanged = false;
      this.fileSizeError = false;
      this.newFilePreviewUrl = null;
    },
    
    closeModal() {
      // cleanup autosave/listeners before closing
      this.removeUploadListener()
      this.stopDraftUpdateTimer()
      this.draftId = null
      this.draftCreated = false
      this.filesPreview = []
      this.$emit('closeModal');
    },
    

    clearNewFile() {
      if (this.newFilePreviewUrl) {
        URL.revokeObjectURL(this.newFilePreviewUrl);
        this.newFilePreviewUrl = null;
      }
      this.file = null;
      this.$refs.fileInput.value = '';
      this.fileChanged = true;
      this.fileSizeError = false;
    },
    
    downloadAttachment() {
      if (!this.existingAttachment?.url) return;
      window.open(this.existingAttachment.url, '_blank');
    },
    
    removeAttachment() {
      this.existingAttachment = null;
      this.fileChanged = true;
    },
    
    async saveApproval() {
      this.loading = true;
      
      try {
        const formData = new FormData();
        formData.append('task_id', this.approvalData.taskId);
        formData.append('subject_id', this.approvalData.subjectId);
        formData.append('estado', this.approvalData.estado);
        formData.append('comentario', this.approvalData.comentario);
        formData.append('entrega_id', this.approvalData.entregaId);
        // If we used draft uploads, send draft_id so backend reassigns files.
        if (this.draftId) formData.append('draft_id', this.draftId)

        if (this.fileChanged) {
          // If user removed existing attachment and no preview remains, tell backend to remove it
          if (!this.filesPreview.length && !this.file && this.existingAttachment) {
            formData.append('remove_attachment', '1')
          }
        }
        
        const response = await setTaskApproval(formData);
        
        if (response.code === 200) {
          this.showSuccessMessage('Aprobación guardada exitosamente');
          this.$emit('savedTask');
        } else {
          throw new Error(response.message || 'Error al guardar la aprobación');
        }
      } catch (error) {
        console.error('Error saving approval:', error);
        this.showErrorMessage(error.message || 'Error al guardar la aprobación');
      } finally {
        this.loading = false;
      }
    },

    // ---- Draft & upload helpers ----
    mountedSetup() {
      // debounce autosave on comentario
      if (!this.handleFormChangeDebounced) {
        this.handleFormChangeDebounced = _debounce(() => this.checkAndCreateDraft(), 1000)
      }
      this.setupUploadListener()
    },

    setupUploadListener() {
      // Make registration idempotent so reopening the dialog cannot create
      // duplicate handlers, while preserving listeners owned by other views.
      EventBus.$off('completedFileUpload', this.handleCompletedFileUpload)
      EventBus.$on('completedFileUpload', this.handleCompletedFileUpload)
    },

    removeUploadListener() {
      EventBus.$off('completedFileUpload', this.handleCompletedFileUpload)
    },

    handleCompletedFileUpload(payload) {
      console.log('completedFileUpload event received:', payload)
      try {
        if (!payload) return
        // If upload was started for this draft, attach the returned id/url to preview
        if (payload.uuid) {
          const idx = this.filesPreview.findIndex(f => f.uuid === payload.uuid)
          if (idx !== -1) {
            this.$set(this.filesPreview[idx], 'id', payload.file.id)
            if (payload.file.pathmedia && payload.file.pathmedia.path) this.$set(this.filesPreview[idx], 'src', payload.file.pathmedia.path)
            this.existingAttachment = { id: payload.file.id, name: payload.file.name || payload.file.filename, url: payload.file.pathmedia?.path }
          }
        }
      } catch (e) { console.error(e) }
    },

    checkAndCreateDraft() { if (!this.draftCreated && !this.draftId) this.createDraft() },

    async createDraft(force_create = false) {
      if (this.draftCreated || this.draftId) return null
      if ((!this.approvalData.comentario || this.approvalData.comentario.trim() === '') && !force_create) return null
      this.draftCreated = true
      const draftData = {
        type: 11,
        project_id: this.$store.projectsStore.state.selectedProject.id,
        type_id: this.approvalData.taskId || this.approvalData.entregaId,
        data: JSON.stringify({ ...this.approvalData })
      }
      try {
        const response = await createDraftAPI(draftData)
        if (response.code === 200) {
          this.draftId = response.draft.id
          this.startDraftUpdateTimer()
          EventBus.$emit('reloadDraftList')
          return this.draftId
        }
        return null
      } catch (error) { this.draftCreated = false; throw error }
    },

    startDraftUpdateTimer() {
      if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
      this.draftSaveTimer = setInterval(() => { this.updateDraft() }, this.draftUpdateInterval * 1000)
    },

    async updateDraft() {
      if (!this.draftId) return
      await updateDraftAPI({
        id: this.draftId,
        type: 11,
        project_id: this.$store.projectsStore.state.selectedProject.id,
        type_id: this.approvalData.taskId || this.approvalData.entregaId,
        data: JSON.stringify({ ...this.approvalData })
      })
    },

    async loadDraft(draft) {
      if (draft && draft.data) {
        const data = (typeof draft.data === 'string') ? JSON.parse(draft.data) : draft.data
        // merge saved draft data into current approvalData
        this.approvalData = { ...this.approvalData, ...data }

        const filePreviews = []
        if (draft.files && Array.isArray(draft.files)) {
          for (const file of draft.files) {
            if (file.mime_type && file.mime_type.includes('image') && file.pathmedia?.path) {
              filePreviews.push({ src: file.pathmedia.path, id: file.id, name: file.name })
            } else {
              filePreviews.push({ noImage: true, id: file.id, name: file.name })
            }
          }
        }
        this.filesPreview = filePreviews
        this.draftId = draft.id
        this.draftCreated = true
        this.startDraftUpdateTimer()
        this.$store.globalStore.dispatch('addMessage', {
          message: 'Borrador cargado correctamente.', title: 'Borrador Cargado',
          created: new Date(), type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3'
        })
      }
    },

    stopDraftUpdateTimer() {
      if (this.draftSaveTimer) {
        clearInterval(this.draftSaveTimer)
        this.draftSaveTimer = null
      }
    },

    async onFileChange(event) {
      const selected = event.target.files[0]
      if (!selected) return
      if (selected.size >= 10000000) {
        this.fileSizeError = true
        this.$refs.fileInput.value = ''
        return
      }
      this.fileSizeError = false
      this.fileChanged = true
      // ensure draft exists
			console.log('draftId:', this.draftId, 'draftCreated:', this.draftCreated)
      if (!this.draftId && !this.draftCreated) await this.createDraft(true)
      const uuid = uuidv4()
      if (selected.type.startsWith('image/')) {
        const b64 = await this.toBase64(selected)
        this.filesPreview.push({ src: b64, name: selected.name, uuid })
      } else {
        this.filesPreview.push({ noImage: true, name: selected.name, uuid })
      }
      this.$store.globalStore.commit('setFilesToUploadCenter', {
        type: 13,
        payload: { draft_id: this.draftId, project_id: this.$store.projectsStore.state.selectedProject.id, uuid },
        description: this.approvalData.subject?.name || 'Aprobación', total: selected.size, uuid, file: selected
      })
    },

    toBase64(file) {
      return new Promise(resolve => {
        const r = new FileReader(); r.readAsDataURL(file)
        r.onload = () => resolve(r.result)
        r.onerror = () => resolve(null)
      })
    },

    removePreviewAttachment(file, index) {
      // Remove preview and file if it exists on server
      if (file.id) deleteFile({ file_id: file.id })
      this.filesPreview.splice(index, 1)
      if (this.existingAttachment && this.existingAttachment.id === file.id) this.existingAttachment = null
      this.fileChanged = true
    },
    
    showSuccessMessage(message) {
      this.$store.globalStore.dispatch('addMessage', {
        message,
        title: 'Éxito',
        created: new Date(),
        type: 'success',
        icon: 'mdi-check-circle',
        color: '#3EBFA3'
      });
    },
    
    showErrorMessage(message) {
      this.$store.globalStore.dispatch('addMessage', {
        message,
        title: 'Error',
        created: new Date(),
        type: 'error',
        icon: 'mdi-alert-circle',
        color: '#F44336'
      });
    }
  }
};
</script>

<style scoped>
/* ── Card ── */
.am-card {
  border-radius: 10px !important;
  overflow: hidden;
}

/* ── Header azul ── */
.am-header {
  background-color: #2D4B95;
  padding: 14px 16px 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.am-header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.am-role-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.28);
  border-radius: 20px;
  padding: 2px 8px 2px 6px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.02em;
  width: fit-content;
}
.am-task-name {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
  white-space: normal;
  word-break: break-word;
}
.am-close-btn {
  flex-shrink: 0;
  margin-top: 1px;
}

/* ── Body ── */
.am-body {
  padding: 16px 16px 8px !important;
}
.am-field-label {
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}
.am-optional {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: #9CA3AF;
}

/* ── Botones de estado ── */
.am-status-group {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.am-status-btn {
  display: inline-flex;
  align-items: center;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1.5px solid #E5E7EB;
  background: #F9FAFB;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  cursor: pointer;
  transition: all 0.15s;
  line-height: 1;
}
.am-status-btn:hover:not(:disabled) {
  border-color: #2D4B95;
  color: #2D4B95;
  background: #EEF2FF;
}
.am-status-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.am-status-btn--active.am-status-btn--aprobado {
  border-color: #2e7d32;
  color: #2e7d32;
  background: #F0FFF4;
}
.am-status-btn--active.am-status-btn--observado {
  border-color: #e65100;
  color: #e65100;
  background: #FFF8F0;
}
.am-status-btn--active.am-status-btn--rechazado {
  border-color: #c62828;
  color: #c62828;
  background: #FFF0F0;
}

/* ── Textarea ── */
.am-textarea { margin-top: 0; }

/* ── Divider interno ── */
.am-divider {
  border-top: 1px solid #F3F4F6;
  margin: 12px 0;
}

/* ── Adjunto existente (no-imagen) ── */
.am-attachment {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  background: #F3F4F6;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  margin-bottom: 8px;
}
.am-attachment-name {
  font-size: 12px;
  color: #374151;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Preview imagen ── */
.am-image-preview-wrap {
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
  background: #F9FAFB;
}
.am-image-link {
  display: block;
  position: relative;
  overflow: hidden;
  max-height: 160px;
}
.am-image-preview {
  width: 100%;
  height: 160px;
  object-fit: cover;
  display: block;
  transition: transform 0.2s ease;
}
.am-image-link:hover .am-image-preview {
  transform: scale(1.02);
}
.am-image-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}
.am-image-link:hover .am-image-overlay {
  background: rgba(0,0,0,0.32);
}
.am-image-overlay .v-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
}
.am-image-link:hover .am-image-overlay .v-icon {
  opacity: 1;
}
.am-image-meta {
  display: flex;
  align-items: center;
  padding: 5px 8px;
  border-top: 1px solid #E5E7EB;
  gap: 4px;
}
.am-image-meta .am-attachment-name {
  font-size: 11px;
}

/* ── Botón subir archivo ── */
.am-upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1.5px dashed #CBD5E1;
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  color: #4B5563;
  cursor: pointer;
  transition: all 0.15s;
  line-height: 1;
}
.am-upload-btn:hover:not(:disabled) {
  border-color: #2D4B95;
  color: #2D4B95;
  background: #EEF2FF;
}
.am-upload-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ── Error tamaño ── */
.am-file-error {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #c62828;
  margin-bottom: 6px;
}

/* ── Acciones ── */
.am-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  padding: 8px 12px 12px;
  border-top: 1px solid #F3F4F6;
}
</style>